import React, { useState, useEffect } from 'react';
import axios from 'axios';

const UserAvatar = () => (
  <div className="w-8 h-8 bg-sw-gray-100 border border-sw-gray-200 rounded-full flex items-center justify-center">
    <span className="text-sw-gray-500 text-[10px] font-medium">YOU</span>
  </div>
);

const BotAvatar = () => (
  <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center">
    <img 
      src="/images/smallchat-avatar.jpg"
      alt="SmallWorld"
      className="w-8 h-8 object-cover"
    />
  </div>
);

const ResultItem = ({ result }) => (
  <div className="py-1">
    <span className="font-medium">{result.source_name}</span> has a{' '}
    <span className="font-medium">{result.relationship_strength}</span> relationship with{' '}
    <span className="font-medium">{result.friend_name}</span>{' '}
    <span>({result.friend_level} | {result.friend_buying_center}) at{' '}</span>
    <span className="font-medium">{result.friend_company}</span>
  </div>
);

const BotMessage = ({ response }) => {
  // Handle structured response
  if (response && (response.best_matches || response.close_matches)) {
    return (
      <div className="flex justify-start items-start space-x-2">
        <BotAvatar />
        <div className="bg-sw-blue-50 rounded-xl py-2 px-4 max-w-[80%] shadow-sm">
          <div className="space-y-4">
            {response.best_matches?.length > 0 ? (
              <div>
                <div className="space-y-1">
                  {response.best_matches.map((result, idx) => (
                    <ResultItem key={`best-${idx}`} result={result} />
                  ))}
                </div>
              </div>
            ) : response.close_matches?.length > 0 ? (
              <>
                <p className="text-sw-gray-500">I didn't find any exact results, but this might be close to what you're looking for.</p>
                <div className="space-y-1">
                  {response.close_matches.map((result, idx) => (
                    <ResultItem key={`close-${idx}`} result={result} />
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sw-gray-500">I was unable to find any results. Please try another question.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Handle string response (fallback)
  return (
    <div className="flex justify-start items-start space-x-2">
      <BotAvatar />
      <div className="bg-sw-blue-50 rounded-xl py-2 px-4 max-w-[80%] shadow-sm">
        <pre className="whitespace-pre-wrap font-sans text-sw-gray-600">{response}</pre>
      </div>
    </div>
  );
};

const TypingIndicator = () => (
  <div className="flex items-start space-x-2">
    <BotAvatar />
    <div className="bg-sw-gray-50 rounded-lg py-3 px-4">
      <div className="flex space-x-1">
        <div className="w-2 h-2 bg-sw-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
        <div className="w-2 h-2 bg-sw-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
        <div className="w-2 h-2 bg-sw-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
      </div>
    </div>
  </div>
);

const Smallchat = () => {
  const [query, setQuery] = useState('');
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const inputRef = React.useRef(null);
  const messagesEndRef = React.useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [responses, isLoading]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const suggestedPrompts = [
    "Do I have any relationships at Amazon?",
    "Do I have any very strong relationships with executives?",
    "Do I have any relationships in the senior-level at Oracle?"
  ];

  const sendMessage = async (queryText) => {
    // Immediately add the user message to responses
    const updatedResponses = [...responses, { user: queryText, bot: null }];
    setResponses(updatedResponses);
    setQuery('');
    setIsLoading(true);

    try {
      // this could be an environment variable, but since this is built as a static site the env vars
      // are interpreted at BUILD time, not run time. Any time the api host changes, the site needs to be
      // rebuilt and redeployed
      const api_host = "chatbotapi.smallworld.engineering";
      const response = await axios.post(
        `https://${api_host}/api/query`,
        { query: queryText },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      
      // Update the last response with the bot's reply
      setResponses(prev => {
        const updatedResponses = [...prev];
        updatedResponses[updatedResponses.length - 1].bot = response.data;
        return updatedResponses;
      });
    } catch (error) {
      console.error("Error sending message:", error);
      let errorMessage = "Sorry, there was an error processing your request.";
      
      // Get more specific error message if available
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      // Update the last response with the error message
      setResponses(prev => {
        const updatedResponses = [...prev];
        updatedResponses[updatedResponses.length - 1].bot = errorMessage;
        return updatedResponses;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && query.trim()) {
      sendMessage(query);
    }
  };

  return (
    <div className="min-h-screen w-full bg-sw-gray-50">
      <div className="max-w-5xl mx-auto min-h-screen p-8">
        <div className="flex flex-col h-[calc(100vh-4rem)]">
          <div className="flex-1 overflow-y-auto mb-4 space-y-4 p-4 bg-white rounded-lg shadow-lg">
            {/* Always show header and suggested questions */}
            <div className="space-y-2">
              <h1 className="text-sw-blue-400 text-xl font-bold">
                Hello and welcome to the SmallWorld Bot!
              </h1>
              <p className="text-sw-gray-700 pb-2 max-w-2xl">
                Ask the SmallWorld bot about the relationships your Connector has with prospects you're trying to get warm intros to.
              </p>
              <div>
                <p className="text-sw-gray-500 pb-2 capitalize">Suggested questions:</p>
                <div className="space-y-2">
                  {suggestedPrompts.map((prompt, index) => (
                    <button
                      key={index}
                      onClick={() => sendMessage(prompt)}
                      className="text-left px-4 py-2 bg-sw-blue-100 hover:bg-sw-blue-400 rounded-xl border border-sw-blue-200 transition-all text-sw-blue-600 hover:border-sw-blue-400 hover:text-white"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Messages section */}
            {responses.map((res, index) => (
              <div key={index} className="space-y-4">
                <div className="flex justify-end items-start space-x-2">
                  <div className="bg-sw-blue-400 text-white rounded-xl py-2 px-4 max-w-[80%]">
                    {res.user}
                  </div>
                  <UserAvatar />
                </div>
                {res.bot !== null && <BotMessage response={res.bot} />}
              </div>
            ))}
            
            {isLoading && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>

          <div className="flex flex-col p-4 bg-white border border-sw-gray-100 rounded-lg shadow-xl">
            <div className="flex gap-2 mb-2">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask SmallWorld..."
                className="flex-1 p-2 pl-4 border border-sw-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sw-blue-400 bg-sw-gray-50"
                disabled={isLoading}
              />
              <button
                onClick={() => query.trim() && sendMessage(query)}
                disabled={!query.trim() || isLoading}
                className="px-6 py-2 bg-sw-blue-400 text-white capitalize hover:cursor-pointer rounded-lg hover:bg-sw-blue-500 disabled:bg-sw-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Sending' : 'Send'}
              </button>
            </div>
            
            <div className="flex flex-wrap items-center text-sm">
              <span className="mr-2 text-sw-gray-400">Ex:</span>
              {suggestedPrompts.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => sendMessage(prompt)}
                  className="mr-2 text-sw-gray-400 hover:text-sw-gray-700 cursor-pointer transition-colors"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Smallchat;